import React, { useState, useMemo } from 'react';
import { FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { IconType } from 'react-icons';

interface VersionStat {
  version: string;
  today: number | null;
  yesterday: number | null;
  lastWeek: number | null;
  total: number | null;
}

interface VersionTotals {
  today: number;
  yesterday: number;
  lastWeek: number;
  total: number;
}

interface VersionStatsTableProps {
  title: string;
  stats: VersionStat[];
  /** Verilmezse footer toplamları sadece görünen satırlardan hesaplanır. Verilirse (ör. tüm versiyonlar) Bugün / Dün / Hafta / Toplam bu veriden hesaplanır. */
  totalsFromStats?: VersionStat[];
  loading: boolean;
  detailLink: string;
}

const VersionStatsTable: React.FC<VersionStatsTableProps> = ({
  title,
  stats,
  totalsFromStats,
  loading,
}) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Handle sort
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort stats
  const sortedStats = useMemo(() => {
    if (!sortColumn) return stats;

    return [...stats].sort((a, b) => {
      if (sortColumn === 'version') {
        // Semantic version sorting
        const parseVersion = (v: string): number[] => {
          return v.split('.').map(num => parseInt(num, 10) || 0);
        };
        const aParts = parseVersion(a.version);
        const bParts = parseVersion(b.version);
        const maxLength = Math.max(aParts.length, bParts.length);
        for (let i = 0; i < maxLength; i++) {
          const aPart = aParts[i] || 0;
          const bPart = bParts[i] || 0;
          if (aPart !== bPart) {
            return sortDirection === 'asc' ? aPart - bPart : bPart - aPart;
          }
        }
        return 0;
      }

      // For numeric columns
      let aValue: number;
      let bValue: number;

      switch (sortColumn) {
        case 'today':
          aValue = a.today ?? 0;
          bValue = b.today ?? 0;
          break;
        case 'yesterday':
          aValue = a.yesterday ?? 0;
          bValue = b.yesterday ?? 0;
          break;
        case 'lastWeek':
          aValue = a.lastWeek ?? 0;
          bValue = b.lastWeek ?? 0;
          break;
        case 'total':
          aValue = a.total ?? 0;
          bValue = b.total ?? 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [stats, sortColumn, sortDirection]);

  const calculateTotals = (rows: VersionStat[]): VersionTotals => {
    return {
      today: rows.reduce((sum, stat) => sum + (stat.today ?? 0), 0),
      yesterday: rows.reduce((sum, stat) => sum + (stat.yesterday ?? 0), 0),
      lastWeek: rows.reduce((sum, stat) => sum + (stat.lastWeek ?? 0), 0),
      total: rows.reduce((sum, stat) => sum + (stat.total ?? 0), 0),
    };
  };

  const totals = calculateTotals(totalsFromStats ?? sortedStats);

  const showLoadingPlaceholder = loading && stats.length === 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
      <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">{title}</h3>
      {showLoadingPlaceholder ? (
        <div className="h-[300px] flex items-center justify-center rounded-xl border border-dashed border-gray-200 dark:border-gray-700 text-slate-500 dark:text-slate-400 font-medium">
          Loading...
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th 
                  className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('version')}
                >
                  <div className="flex items-center gap-2">
                    <span>VERSİYON</span>
                    {sortColumn === 'version' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('today')}
                >
                  <div className="flex items-center gap-2">
                    <span>Bugün</span>
                    {sortColumn === 'today' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('yesterday')}
                >
                  <div className="flex items-center gap-2">
                    <span>Dün</span>
                    {sortColumn === 'yesterday' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('lastWeek')}
                >
                  <div className="flex items-center gap-2">
                    <span>Son 1 Haftada</span>
                    {sortColumn === 'lastWeek' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('total')}
                >
                  <div className="flex items-center gap-2">
                    <span>Toplam</span>
                    {sortColumn === 'total' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedStats.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-2 text-center text-slate-500 dark:text-slate-400">
                    Veri bulunamadı
                  </td>
                </tr>
              ) : (
                <>
                  {sortedStats.map((stat, index) => (
                    <tr key={index} className="dark:hover:bg-gray-700">
                      <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{stat.version}</td>
                      <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{(stat.today ?? 0).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{(stat.yesterday ?? 0).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{(stat.lastWeek ?? 0).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{(stat.total ?? 0).toLocaleString('tr-TR')}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                    <td className="px-4 py-2 text-slate-800 dark:text-white">Toplam</td>
                    <td className="px-4 py-2 text-slate-800 dark:text-white">{(totals.today ?? 0).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-2 text-slate-800 dark:text-white">{(totals.yesterday ?? 0).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-2 text-slate-800 dark:text-white">{(totals.lastWeek ?? 0).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-2 text-slate-800 dark:text-white">{(totals.total ?? 0).toLocaleString('tr-TR')}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VersionStatsTable;
